<template>
  <div class="mb-2">
    <SidebarSection ref="section" title="Inlandsflüge" collapsedOnMount>
      <template #actions>
        <b-button
          v-if="!localInlandsflugExists"
          variant="outline-primary"
          size="sm"
          class="hinzufuegen-btn"
          @click="onAdd"
        >
          <Plus :size="16" />
          Inlandsflug hinzufügen
        </b-button>
      </template>
      <div v-if="inlandsfluege?.length > 0">
        <div v-for="(inlandsflug, i) in inlandsfluege" :key="inlandsflug.id" class="inlandsflug">
          <div class="inlandsflug__header">
            <span class="inlandsflug__index">{{ i + 1 }}</span>
            <span class="inlandsflug__titel">Inlandsflug</span>
            <span
              v-if="inlandsflug.matchingSeasonInlandsflug === null"
              v-b-tooltip.hover
              title="Kein passender Saison-Inlandsflug gefunden."
            >
              <Asterisk :size="14" class="text-warning" />
            </span>
            <button
              type="button"
              class="inlandsflug__delete"
              title="Inlandsflug löschen"
              @click="$emit('delete', inlandsflug.id)"
            >
              <Trash2 :size="16" />
            </button>
            <div class="inlandsflug__meta">
              <span class="inlandsflug__meta-label">Tag</span>
              <b-form-input
                v-model="inlandsflug.tag"
                :disabled="!inlandsflug?.isOnlyLocal"
                class="inlandsflug__tag"
                type="number"
                step="1"
                min="0.00"
                max="1000.00"
                :number="true"
                placeholder="-"
              ></b-form-input>
              <span class="inlandsflug__meta-label">Verkehrstag</span>
              <span class="inlandsflug__meta-wert">
                {{ getVerkehrstagOfInlandsflug(activeReisetermin, inlandsflug) }}
              </span>
              <span class="inlandsflug__meta-label">Datum</span>
              <span class="inlandsflug__meta-wert">
                {{ getDateOfIlandsflug(activeReisetermin, inlandsflug) }}
              </span>
            </div>
          </div>
          <SidebarFieldGrid>
            <SidebarFieldRow
              label="Einkauf"
              :column="1"
              :saisonwert="saisonwert(inlandsflug, 'einkauf')?.toUpperCase()"
            >
              <AppSelect
                v-model="inlandsflug.einkauf"
                sort="none"
                :options="einkaufOptions"
                label="Einkauf"
                placeholder="-"
                hide-search
                clearable
                class="einkauf-select"
              />
            </SidebarFieldRow>
            <SidebarFieldRow
              label="Frühester Abflug"
              :column="2"
              :saisonwert="saisonwert(inlandsflug, 'fruehesterAbflug', true)"
            >
              <date-picker
                class="inlandsflug__zeit"
                v-model="inlandsflug.fruehesterAbflug"
                type="time"
                input-class="form-control"
                format="HH:mm"
                value-type="format"
                :append-to-body="false"
              ></date-picker>
            </SidebarFieldRow>
            <SidebarFieldRow label="Abflughafen" :column="1">
              <AirportsSucheMultiselect
                class="w-100"
                :disabled="!inlandsflug?.isOnlyLocal"
                v-model="inlandsflug.abflughafenIataCode"
                :isMultipleAllowed="true"
                :openDirection="'top'"
                :filterCountry="activeReisetermin?.reiseterminkuerzel?.substring(0, 2)"
                height="32px"
              ></AirportsSucheMultiselect>
            </SidebarFieldRow>
            <SidebarFieldRow
              label="Späteste Ankunft"
              :column="2"
              :saisonwert="saisonwert(inlandsflug, 'spaetesteAnkunft', true)"
            >
              <date-picker
                class="inlandsflug__zeit"
                v-model="inlandsflug.spaetesteAnkunft"
                :disabled="inlandsflug.minimaleUmsteigezeit != '' && inlandsflug.minimaleUmsteigezeit != null"
                type="time"
                input-class="form-control"
                format="HH:mm"
                value-type="format"
                :append-to-body="false"
              ></date-picker>
            </SidebarFieldRow>
            <SidebarFieldRow
              label="Zielflughafen"
              :column="1"
              :saisonwert="saisonwert(inlandsflug, 'zielflughafenIataCode')"
            >
              <AirportsSucheMultiselect
                class="w-100"
                v-model="inlandsflug.zielflughafenIataCode"
                :isMultipleAllowed="false"
                clearable
                :openDirection="'top'"
                :filterCountry="activeReisetermin?.reiseterminkuerzel?.substring(0, 2)"
                height="32px"
              ></AirportsSucheMultiselect>
            </SidebarFieldRow>
            <SidebarFieldRow
              label="Minimale Umsteigezeit"
              :column="2"
              :saisonwert="saisonwert(inlandsflug, 'minimaleUmsteigezeit')"
            >
              <b-form-input
                v-model="inlandsflug.minimaleUmsteigezeit"
                :disabled="inlandsflug.spaetesteAnkunft != null"
                class="inlandsflug__umsteigezeit"
                type="number"
                step="1"
                min="0.00"
                :number="true"
                placeholder="-"
              ></b-form-input>
            </SidebarFieldRow>
          </SidebarFieldGrid>
        </div>
      </div>
      <div v-else class="inlandsflug-empty">Noch keine Inlandsflüge angelegt.</div>
    </SidebarSection>
  </div>
</template>

<script>
import { Asterisk, Plus, Trash2 } from 'lucide-vue';
import DatePicker from 'vue2-datepicker';
import { format, add, getISODay } from 'date-fns';
import AirportsSucheMultiselect from '@/components/common/airports-suche-multiselect.vue';
import AppSelect from '@/components/common/app-select/app-select.vue';
import SidebarSection from '@/components/common/sidebar-section/sidebar-section.vue';
import SidebarFieldGrid from '@/components/common/sidebar-section/sidebar-field-grid.vue';
import SidebarFieldRow from '@/components/common/sidebar-section/sidebar-field-row.vue';

export default {
  name: 'SidebarTerminplanungInlandsfluege',
  components: {
    Asterisk,
    Plus,
    Trash2,
    DatePicker,
    AirportsSucheMultiselect,
    AppSelect,
    SidebarSection,
    SidebarFieldGrid,
    SidebarFieldRow,
  },
  props: {
    // kopie der Inlandsflüge
    inlandsfluege: { type: Array, default: () => [] },
    // Der aktive Reisetermin für Länderfilter sowie Verkehrstag- und Datumsberechnung
    activeReisetermin: { type: Object, default: null },
    // Steuert die Sichtbarkeit des Hinzufügen-Buttons
    localInlandsflugExists: { type: Boolean, default: false },
  },
  data() {
    return {
      einkaufOptions: [
        { value: 'Skr', text: 'SKR' },
        { value: 'Dmc', text: 'DMC' },
      ],
    };
  },
  methods: {
    // Öffnet die Inlandsflug-Sektion und meldet dem Parent, dass ein lokaler Inlandsflug angelegt werden soll
    onAdd() {
      this.$refs.section.open();
      this.$emit('add');
    },
    // Liefert den abweichenden Saisonwert eines Feldes, sonst null
    // Lokal angelegte Inlandsflüge und Flüge ohne passenden Saisonflug haben keinen Vergleichswert
    saisonwert(inlandsflug, feld, alsZeit = false) {
      const saison = inlandsflug.matchingSeasonInlandsflug;
      if (inlandsflug?.isOnlyLocal || !saison) {
        return null;
      }
      const kuerzen = wert => (alsZeit ? wert?.substring(0, 5) : wert);
      const saisonWert = kuerzen(saison[feld]);
      if (saisonWert === kuerzen(inlandsflug.valuesBeforeEdit?.[feld])) {
        return null;
      }
      return saisonWert;
    },
    // Berechnet den Verkehrstag (ISO-Wochentag) eines Inlandsflugs relativ zum Abreisedatum
    getVerkehrstagOfInlandsflug(reisetermin, inlandsflug) {
      const dayOfInlandsflug = add(new Date(reisetermin.abreisedatum), { days: +inlandsflug.tag - 1 });
      return getISODay(dayOfInlandsflug);
    },
    // Berechnet das konkrete Datum eines Inlandsflugs relativ zum Abreisedatum
    getDateOfIlandsflug(reisetermin, inlandsflug) {
      const dateOfInlandsfluge = add(new Date(reisetermin.abreisedatum), { days: +inlandsflug.tag - 1 });
      return format(dateOfInlandsfluge, 'dd.MM.yyyy');
    },
  },
};
</script>

<style lang="scss" scoped>
.hinzufuegen-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.inlandsflug + .inlandsflug {
  border-top: 1px solid var(--border);
}

.inlandsflug:first-child .inlandsflug__header {
  border-radius: var(--radius-md) var(--radius-md) 0 0;
}

.inlandsflug:last-child :deep(.sidebar-field-grid) > :nth-last-child(-n + 2) {
  border-bottom: none;
}

.inlandsflug:last-child :deep(.sidebar-field-grid) > :nth-last-child(2) {
  border-bottom-left-radius: var(--radius-md);
}

.inlandsflug:last-child :deep(.sidebar-field-grid) > :last-child {
  border-bottom-right-radius: var(--radius-md);
}

.inlandsflug__header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-bottom: 1px solid var(--border);
  background-color: var(--surface-sunken);
}

.inlandsflug__index {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  height: 20px;
  width: 20px;
  border-radius: 50%;
  font-size: 12px;
  font-weight: 600;
  color: var(--surface);
  background-color: var(--text);
}

.inlandsflug__titel {
  font-size: 14px;
  font-weight: 600;
  color: var(--text);
}

.inlandsflug__delete {
  display: flex;
  align-items: center;
  padding: 2px;
  border: 0;
  border-radius: var(--radius-sm);
  background: none;
  color: var(--text-subtle);
  cursor: pointer;

  &:hover {
    color: var(--danger);
    background-color: #f64e601a;
  }
}

.inlandsflug__meta {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
}

.inlandsflug__meta-label {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: var(--text-muted);
}

.inlandsflug__meta-wert {
  font-size: 13px;
  font-weight: 600;
  color: var(--text);
}

.inlandsflug__tag,
.inlandsflug__umsteigezeit {
  height: var(--control-h-sm);
  padding: 4px 8px;
  font-size: 13px;
  font-weight: 500;
  border-radius: var(--radius-sm);
}

.inlandsflug__tag {
  width: 72px;
}

.inlandsflug__umsteigezeit {
  width: 100%;
}

.inlandsflug__zeit {
  width: 100%;
  min-width: 0;
  height: var(--control-h-sm);
}

.inlandsflug__zeit :deep(.form-control) {
  height: var(--control-h-sm);
  min-height: var(--control-h-sm);
  padding: 4px 26px 4px 8px;
  font-size: 13px;
  border-radius: var(--radius-sm);
}

.inlandsflug-empty {
  padding: 24px;
  text-align: center;
  font-size: 13px;
  font-weight: 500;
  color: var(--text-muted);
}

.einkauf-select {
  --app-select-height: var(--control-h-sm);
  --app-select-font-size: 13px;
}

.tooltip {
  opacity: 1 !important;
}

:deep(.mx-datepicker-content) {
  margin: 0;
}
</style>
